import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { scanApi } from '../utils/scanApi';
import { brand } from '../theme/brandTheme';
import { useAuth } from '../contexts/AuthContext';
import BackgroundFooter from "./BackgroundFooter";
import { Col, Row } from "react-bootstrap";
import Menubar from "./Menubar";
import NavigationBar from "./Nav";
import ScanConsentModal from "./ScanConsentModel";
import DomainManagement from './DomainManagement';

const BRAND = {
  primary: "#6BCE8B",
  primaryDark: "#4EAD6E",
  primaryLight: "#E8F8EE",

  secondary: "#035EA5",
  secondaryDark: "#024A82",
  secondaryLight: "#E6F0F9",

  white: "#FFFFFF",

  gray50: "#F8FAFC",
  gray100: "#F1F5F9",
  gray200: "#E2E8F0",
  gray300: "#CBD5E1",
  gray400: "#94A3B8",
  gray500: "#64748B",
  gray600: "#475569",
  gray900: "#0F172A",
};

const FONT = "'Inter', sans-serif";
const severityColors = {
  CRITICAL: "#9B1C1C",
  HIGH: "#DC2626",
  MEDIUM: "#A16207",
  LOW: "#4EAD6E",
  INFO: "#64748B",
};

// ─── Inject keyframes + responsive styles ─────────────────────────
(() => {
  if (document.getElementById("sq-dast-v2")) return;
  const el = document.createElement("style");
  el.id = "sq-dast-v2";
  el.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes fadeInUp {
      from { opacity: 0; transform: translateY(16px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }
    @keyframes shimmerAccent {
      0%   { background-position: -200% 0; }
      100% { background-position: 200% 0; }
    }
    @keyframes slideDown {
      from { opacity: 0; max-height: 0; }
      to   { opacity: 1; max-height: 600px; }
    }
    @keyframes countUp {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes glowPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(3,94,165,0); }
      50% { box-shadow: 0 0 20px 4px rgba(3,94,165,0.15); }
    }
    @keyframes toastSlide {
      from { opacity: 0; transform: translateX(40px); }
      to   { opacity: 1; transform: translateX(0); }
    }

    /* ── Page & Layout ── */
    .dast-v2 { animation: fadeIn 0.3s ease; }
    .dast-v2-inner { scroll-behavior: smooth; }
    .dast-v2-inner::-webkit-scrollbar { width: 5px; }
    .dast-v2-inner::-webkit-scrollbar-track { background: transparent; }
    .dast-v2-inner::-webkit-scrollbar-thumb { background: ${brand.colors.surfaceBorder}; border-radius: 3px; }

    /* ── Cards ── */
    .dast-card { transition: all 0.3s cubic-bezier(0.4,0,0.2,1); }
    
    /* AFTER — green glow shadow only, no background fill */
.dast-stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 24px rgba(107, 206, 139, 0.45) !important;
  border-color: #6BCE8B !important;
}
    .dast-card-static { transition: border-color 0.2s ease, box-shadow 0.2s ease; }

    /* ── Buttons ── */
    .dast-btn { 
  transition: transform 0.2s ease, filter 0.2s ease;
  position: relative; 
  overflow: hidden; 
}
    .dast-btn:hover { transform: translateY(-1px); }
    .dast-btn:active { transform: translateY(0) scale(0.98); }
   
    /* .dast-btn-glow:hover { animation: glowPulse 1.5s ease infinite; } */

    /* ── Table ── */
   .dast-tr {
  transition: background 0.15s ease;
}

.dast-tr:hover {
  background: #F8FAFC !important;
}
    .dast-tr td { transition: color 0.15s ease; }

    /* ── Vuln Row ── */
    .dast-vuln { transition: all 0.2s ease; }
    .dast-vuln:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.08); transform: translateX(2px); }

    /* ── Filter Pill ── */
    .dast-pill { transition: all 0.2s ease; cursor: pointer; user-select: none; }
    .dast-pill:hover { transform: scale(1.06); }
    .dast-pill:active { transform: scale(0.96); }

    /* ── Chart Bar ── */
    .dast-bar { transition: width 1s cubic-bezier(0.4,0,0.2,1); }
    .dast-bar:hover { filter: brightness(1.15); }

    /* ── Modal ── */
    .dast-overlay { animation: fadeIn 0.2s ease; }
    .dast-modal-enter { animation: fadeInUp 0.35s cubic-bezier(0.4,0,0.2,1); }

    /* ── Stagger children animation ── */
    .dast-stagger > * {
      animation: fadeInUp 0.4s cubic-bezier(0.4,0,0.2,1) both;
    }
    .dast-stagger > *:nth-child(1) { animation-delay: 0.05s; }
    .dast-stagger > *:nth-child(2) { animation-delay: 0.1s; }
    .dast-stagger > *:nth-child(3) { animation-delay: 0.15s; }
    .dast-stagger > *:nth-child(4) { animation-delay: 0.2s; }
    .dast-stagger > *:nth-child(5) { animation-delay: 0.25s; }

    /* ── Responsive ── */
    @media (max-width: 1200px) {
      .dast-grid-5 { grid-template-columns: repeat(3, 1fr) !important; }
      .dast-form-flex { flex-wrap: wrap !important; }
      .dast-chart-grid { grid-template-columns: 1fr !important; }
    }
    @media (max-width: 768px) {
      .dast-page-pad { padding: 16px 12px !important; }
      .dast-grid-5 { grid-template-columns: repeat(2, 1fr) !important; }
      .dast-header-flex { flex-direction: column !important; gap: 12px !important; align-items: flex-start !important; }
      .dast-form-flex { flex-direction: column !important; }
      .dast-chart-grid { grid-template-columns: 1fr !important; }
      .dast-detail-grid { grid-template-columns: 1fr !important; }
      .dast-table-wrap { overflow-x: auto; }
      .dast-modal-box { width: 95% !important; padding: 20px !important; max-height: 90vh !important; }
      .dast-risk-grid { grid-template-columns: repeat(3, 1fr) !important; }
    }
    @media (max-width: 480px) {
      .dast-grid-5 { grid-template-columns: 1fr !important; }
      .dast-risk-grid { grid-template-columns: repeat(2, 1fr) !important; }
    }
  `;
  document.head.appendChild(el);
})();

// ═══════════════════════════════════════════════════════════════════
// SVG CHART COMPONENTS (zero-dependency)
// ═══════════════════════════════════════════════════════════════════

// ── Animated Donut ──────────────────────────────────────────────
const DonutChart = ({ data, size = 180, strokeWidth = 24 }) => {
  const r = (size - strokeWidth) / 2;
  const C = 2 * Math.PI * r;
  const cx = size / 2;
  const total = data.reduce((s, d) => s + d.value, 0);
  let cum = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cx} r={r} fill="none"
        stroke={brand.colors.surfaceBorder} strokeWidth={strokeWidth} opacity="0.4" />
      {data.filter(d => d.value > 0).map((d, i) => {
        const pct = d.value / total;
        const dash = pct * C;
        const offset = -(cum / total) * C;
        cum += d.value;
        return (
          <circle key={i} cx={cx} cy={cx} r={r} fill="none"
            stroke={d.color} strokeWidth={strokeWidth}
            strokeDasharray={`${dash} ${C - dash}`}
            strokeDashoffset={offset} strokeLinecap="butt"
            transform={`rotate(-90 ${cx} ${cx})`}
            style={{ transition: 'stroke-dasharray 0.8s ease, stroke-dashoffset 0.8s ease' }} />
        );
      })}
      <text x={cx} y={cx - 6} textAnchor="middle"
        style={{ fontSize: 28, fontWeight: 800, fill: brand.colors.text, fontFamily: brand.font }}>
        {total}
      </text>
      <text x={cx} y={cx + 14} textAnchor="middle"
        style={{ fontSize: 10, fontWeight: 700, fill: brand.colors.textMuted, textTransform: 'uppercase', letterSpacing: 1, fontFamily: brand.font }}>
        Total Alerts
      </text>
    </svg>
  );
};

// ── Horizontal Bar Chart ────────────────────────────────────────
const HBarChart = ({ data }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, width: '100%' }}>
      {data.map((d, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: brand.colors.textSecondary, minWidth: 90, textAlign: 'right' }}>
            {d.label}
          </span>
          <div style={{ flex: 1, height: 22, background: `${brand.colors.surface}`, borderRadius: 6, overflow: 'hidden' }}>
            <div className="dast-bar" style={{
              width: `${(d.value / max) * 100}%`, height: '100%',
              // background: `linear-gradient(90deg, ${d.color}ee, ${d.color}88)`,
              borderRadius: 6, minWidth: d.value > 0 ? 20 : 0,
            }} />
          </div>
          <span style={{ fontSize: 14, fontWeight: 800, color: d.color, minWidth: 28, textAlign: 'right' }}>
            {d.value}
          </span>
        </div>
      ))}
    </div>
  );
};

// ── Scan Overview Mini Chart (for dashboard) ────────────────────
const ScanOverviewChart = ({ stats }) => {
  const items = [
    { label: 'Completed', value: stats.completed, color: brand.colors.primary },
    { label: 'Running', value: stats.running, color: '#0A7FDC' },
    { label: 'Queued', value: stats.queued, color: brand.colors.queued || '#6B7280' },
    { label: 'Failed', value: stats.failed, color: brand.colors.critical },
  ];
  const total = Math.max(stats.total, 1);
  return (
    <div>
      {/* Stacked bar */}
      <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: brand.colors.surface, marginBottom: 16 }}>
        {items.filter(i => i.value > 0).map((item, i) => (
          <div key={i} className="dast-bar" style={{
            width: `${(item.value / total) * 100}%`, height: '100%',
            background: item.color, transition: 'width 0.8s ease',
          }} />
        ))}
      </div>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
        {items.map(item => (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: brand.colors.textSecondary }}>{item.label}</span>
            <span style={{ fontSize: 12, fontWeight: 700, color: brand.colors.text }}>{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════
const S = {
  page: {
  fontFamily: FONT,
  background: BRAND.gray50,
  minHeight: '100vh',
  color: BRAND.gray900,
},
  inner: {
  padding: "24px 24px",
  maxWidth: 1280,
  margin: "0 auto",
},

  // Header
 header: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: 20,
  paddingBottom: 12,
  borderBottom: `1px solid ${BRAND.gray200}`,
},

title: {
  fontSize: 22,
  fontWeight: 700,
  color: BRAND.gray900,
  margin: 0,
},

subtitle: {
  fontSize: 13,
  color: BRAND.gray500,
  marginTop: 2,
},
  headerRight: { display: 'flex', alignItems: 'center', gap: 12 },

  // Form
  formCard: {
   background: BRAND.white,
border: `1px solid ${BRAND.gray200}`,
    borderRadius: "10px",
boxShadow: "0 1px 2px rgba(0,0,0,0.04)", padding: '24px 24px 38px', marginBottom: 24,
    position: 'relative', overflow: 'hidden',
  },
  // formAccent: {
  //   position: 'absolute', top: 0, left: 0, right: 0, height: 3,
  //   // background: `linear-gradient(90deg, ${brand.colors.secondary}, ${brand.colors.primary}, ${brand.colors.secondary})`,
  //   backgroundSize: '200% 100%', animation: 'shimmerAccent 3s linear infinite',
  // },
  formRow: {
  display: 'flex',
  gap: 12,
  alignItems: 'flex-end',
  justifyContent: 'flex-start'
},
  inputGroup: { flex: 1, position: 'relative' },
  label: { display: 'block', fontSize: 11, fontWeight: 700, color: brand.colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8 },
  input: {
    width: '100%', padding: '12px 16px', background: brand.colors.surface,
    border: `1px solid ${brand.colors.surfaceBorder}`, borderRadius: brand.radius.md,
    color: brand.colors.text, fontSize: 14, fontFamily: brand.font,
    outline: 'none', transition: 'all 0.2s ease', boxSizing: 'border-box',
  },
select: {
  padding: '12px 40px 12px 14px',
  background: `#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E") no-repeat calc(100% - 12px) center`,
  backgroundSize: '12px 12px',
  border: `1px solid ${BRAND.gray200}`,
  borderRadius: '8px',
  color: BRAND.gray900,
  fontSize: 13,
  fontFamily: FONT,
  outline: 'none',
  cursor: 'pointer',
  minWidth: 160,
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
},
  badge_domain: (verified) => ({
    position: 'absolute', bottom: -20, left: 0, fontSize: 12,
    display: 'flex', alignItems: 'center', gap: 4,
    color: verified ? brand.colors.primary : brand.colors.high,
    fontWeight: 600,
  }),

  // Buttons
btnPrimary: {
  padding: '10px 18px',
  background: BRAND.secondary,
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: FONT,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
},
  btnOutline: {
  padding: '8px 16px',
  background: '#fff',
  color: BRAND.gray600,
  border: `1px solid ${BRAND.gray200}`,
  borderRadius: '8px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: FONT,
},
  btnAnalyze: {
  padding: '8px 16px',
  background: BRAND.secondaryLight,
  color: BRAND.secondary,
  border: `1px solid ${BRAND.secondaryLight}`,
  borderRadius: '8px',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: FONT,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
},
  btnReanalyze: {
  padding: '6px 14px',
  background: '#fff',
  color: BRAND.gray600,
  border: `1px solid ${BRAND.gray200}`,
  borderRadius: '8px',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: FONT,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
},
  btnDisabled: {
  opacity: 0.5,
  cursor: 'not-allowed',
  background: BRAND.gray100,
  color: BRAND.gray400,
  border: `1px solid ${BRAND.gray200}`,
},

  // Stats
  statsGrid: {
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: 16,
  marginBottom: 20,
},
statCard: (color, active) => ({
  position: "relative",
  overflow: "hidden",
  background: BRAND.white,
  border: `1px solid ${BRAND.gray200}`,
  borderRadius: "10px",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  padding: "20px 16px 16px 20px",
  cursor: "default",
  transition: "all 0.2s ease",
}),
  statAccent: (color) => ({
    position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: color, borderRadius: '4px 0 0 4px',
  }),
  statIconBg: (color) => ({
    width: 36, height: 36, borderRadius: brand.radius.sm, background: 'transparent',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
  }),
  statVal: { fontSize: 32, fontWeight: 600, margin: 0, letterSpacing: -1, lineHeight: 1 },
  statLbl: { fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 6 },

  // Dashboard overview row
  overviewRow: {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 16,
  marginBottom: 20,
},
  overviewCard: {
  background: BRAND.white,
  border: `1px solid ${BRAND.gray200}`,
  borderRadius: "10px",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  padding: 24,
},
  overviewTitle: { fontSize: "14px",
fontWeight: 700,
color: BRAND.gray900, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 },

  // Table
tableCard: {
  background: BRAND.white,
  border: `1px solid ${BRAND.gray200}`,
  borderRadius: "10px",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  overflow: "hidden",
  marginBottom: 24,
},
  tableHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: `1px solid ${brand.colors.divider}` },
  tableTitleText: { fontSize: "14px",
fontWeight: 700,
color: BRAND.gray900,display: 'flex', alignItems: 'center', gap: 8 },
  table: { width: '100%', borderCollapse: 'collapse' },
 th: {
  padding: "12px 20px",
  fontSize: "11px",
  fontWeight: 600,
  color: BRAND.gray500,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  background: BRAND.gray50,
  borderBottom: `1px solid ${BRAND.gray200}`,
},
 td: {
  padding: "14px 20px",
  fontSize: 13,
  color: BRAND.gray900,
  borderBottom: `1px solid ${BRAND.gray100}`,
},

  // Badge
badge: (status) => {
  const map = {
    COMPLETED: {
      bg: BRAND.primaryLight,
      text: BRAND.primaryDark,
      label: "Completed"
    },
    RUNNING: {
      bg: BRAND.secondaryLight,
      text: BRAND.secondary,
      label: "Running"
    },
    QUEUED: {
      bg: BRAND.gray100,
      text: BRAND.gray500,
      label: "Queued"
    },
    FAILED: {
      bg: "#FDE8E8",
      text: "#9B1C1C",
      label: "Failed"
    },
    ANALYZED: {
      bg: BRAND.secondaryLight,
      text: BRAND.secondary,
      label: "Analyzed"
    }
  };

  const s = map[status] || map.QUEUED;

  return {
    padding: "4px 12px",
    borderRadius: "6px",
    fontSize: "11px",
    fontWeight: 600,
    background: s.bg,
    color: s.text,
    border: "none",
  };
},

  // Findings
  findingsSection: { marginTop: 8 },
  findingsHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${brand.colors.divider}` },
  findingsTitle: { fontSize: "14px",
fontWeight: 700,
color: BRAND.gray900, display: 'flex', alignItems: 'center', gap: 10 },
  findingsSubtitle: { fontSize: 13, color: brand.colors.textSecondary, marginTop: 4 },
  findingsSummaryBar: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 },
  findingsSummaryItem: (color, active) => ({
  background: BRAND.white,
  border: `1px solid ${active ? color : BRAND.gray200}`,
  borderRadius: "10px",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  padding: 16,
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.2s ease",
}),
  filterBar: { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' },
 filterPill: (active) => ({
  padding: "6px 12px",
  borderRadius: "6px",
  fontSize: 12,
  fontWeight: 600,
  border: `1px solid ${active ? BRAND.secondary : BRAND.gray200}`,
  background: active ? BRAND.secondaryLight : "#fff",
  color: active ? BRAND.secondary : BRAND.gray600,
  cursor: "pointer",
}),

  // Vuln rows
  vulnRow: (sev) => {
    const bm = { Critical: brand.colors.critical, High: brand.colors.high, Medium: brand.colors.medium, Low: brand.colors.low };
    return { background: brand.colors.cardBg, border: `1px solid ${brand.colors.surfaceBorder}`, borderLeft: `4px solid ${bm[sev] || brand.colors.info}`, borderRadius: brand.radius.md, marginBottom: 10, overflow: 'hidden' };
  },
  vulnHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', cursor: 'pointer' },
  vulnLeft: { display: 'flex', alignItems: 'center', gap: 12, flex: 1 },
  vulnName: { fontSize: 14, fontWeight: 600, color: brand.colors.text },
  vulnMeta: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  vulnBody: { padding: '0 20px 20px', borderTop: `1px solid ${brand.colors.divider}`, animation: 'slideDown 0.3s ease' },
  vulnGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 },
  vulnBlock: { background: brand.colors.surface, borderRadius: brand.radius.sm, padding: 16 },
  vulnBlockLabel: { fontSize: 11, fontWeight: 700, color: brand.colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
  vulnBlockText: { fontSize: 13, color: brand.colors.textSecondary, lineHeight: 1.7 },
  urlChip: { display: 'inline-block', padding: '3px 10px', background: brand.colors.surface, border: `1px solid ${brand.colors.surfaceBorder}`, borderRadius: 4, fontSize: 11, fontFamily: 'monospace', color: brand.colors.textSecondary, marginRight: 6, marginBottom: 4 },
  tag: (color) => ({ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, background: `${color}12`, color, border: `1px solid ${color}25` }),

  // Charts
  chartCard: {
  background: BRAND.white,
  border: `1px solid ${BRAND.gray200}`,
  borderRadius: "10px",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  padding: 20,
},
  chartTitle: { fontSize: 13, fontWeight: 700, color: brand.colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 8 },

  // Modals
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 50, zIndex: 1000, overflowY: 'auto' },
  modal: { background: brand.colors.surface, border: `1px solid ${brand.colors.surfaceBorder}`, borderRadius: brand.radius.xl, width: '90%', maxWidth: 960, maxHeight: '85vh', overflowY: 'auto', padding: 32, marginBottom: 60 },
  modalTitle: { fontSize: 22, fontWeight: 800, marginBottom: 8, letterSpacing: -0.5 },
  riskGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 28 },
  riskItem: (sev) => {
    const m = { critical: { bg: brand.colors.criticalBg, c: brand.colors.critical, b: 'rgba(255,77,106,0.3)' }, high: { bg: brand.colors.highBg, c: brand.colors.high, b: 'rgba(255,140,66,0.3)' }, medium: { bg: brand.colors.mediumBg, c: brand.colors.medium, b: 'rgba(255,209,102,0.3)' }, low: { bg: brand.colors.lowBg, c: brand.colors.low, b: brand.colors.primaryBorder }, informational: { bg: brand.colors.infoBg, c: brand.colors.info, b: 'rgba(3,94,165,0.3)' } };
    const s = m[sev] || m.informational;
    return { background: s.bg, border: `1px solid ${s.b}`, borderRadius: brand.radius.md, padding: 16, textAlign: 'center' };
  },
  vulnCard: (sev) => {
    const bm = { CRITICAL: brand.colors.critical, HIGH: brand.colors.high, MEDIUM: brand.colors.medium, LOW: brand.colors.low };
    return { background: brand.colors.cardBg, border: `1px solid ${brand.colors.surfaceBorder}`, borderLeft: `4px solid ${bm[sev] || brand.colors.info}`, borderRadius: brand.radius.md, padding: 20, marginBottom: 12 };
  },
  vulnTitle2: { fontSize: 15, fontWeight: 600, marginBottom: 8 },
  vulnMeta2: { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
  vulnSec: { fontSize: 13, color: brand.colors.textSecondary, lineHeight: 1.7, marginTop: 8 },

  // Verify modal
  verifyOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  verifyModal: { background: brand.colors.surface, border: `1px solid ${brand.colors.surfaceBorder}`, borderRadius: brand.radius.xl, width: '90%', maxWidth: 600, padding: 32, boxShadow: brand.shadow?.elevated || '0 24px 60px rgba(0,0,0,0.3)' },
  verifyTitle: { fontSize: 20, fontWeight: 800, color: brand.colors.text, marginBottom: 8 },
  verifySubtitle: { fontSize: 14, color: brand.colors.textSecondary, marginBottom: 24 },
  dnsBox: { background: brand.colors.cardBg, border: `1px solid ${brand.colors.surfaceBorder}`, borderRadius: brand.radius.md, padding: 20, marginBottom: 20 },
  dnsLabel: { fontSize: 11, fontWeight: 700, color: brand.colors.textMuted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 },
  dnsValue: { fontFamily: 'monospace', fontSize: 13, color: brand.colors.primary, wordBreak: 'break-all', background: brand.colors.surface, padding: '10px 14px', borderRadius: brand.radius.sm, border: `1px solid ${brand.colors.primaryBorder}`, cursor: 'pointer' },
  verifySteps: { fontSize: 13, color: brand.colors.textSecondary, lineHeight: 1.8, marginBottom: 24 },
  verifyActions: { display: 'flex', gap: 12, justifyContent: 'flex-end' },

  // Toast
  toast: (type) => ({
    position: 'fixed', bottom: 24, right: 24, padding: '14px 24px',
    borderRadius: brand.radius.md, fontSize: 14, fontWeight: 600, fontFamily: brand.font,
    color: type === 'error' ? brand.colors.critical : brand.colors.primary,
    background: type === 'error' ? brand.colors.criticalBg : brand.colors.primaryBg,
    border: `1px solid ${type === 'error' ? 'rgba(255,77,106,0.3)' : brand.colors.primaryBorder}`,
    zIndex: 2000, animation: 'toastSlide 0.35s ease', display: 'flex', alignItems: 'center', gap: 8,
    boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
  }),

  // Misc
  empty: { textAlign: 'center', padding: '80px 40px' },
  emptyIcon: { fontSize: 56, marginBottom: 16, opacity: 0.6 },
  emptyText: { fontSize: 16, color: brand.colors.textSecondary },
  spinner: { display: 'inline-block', width: 16, height: 16, border: `2px solid ${brand.colors.surfaceBorder}`, borderTopColor: brand.colors.primary, borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
scanSelector: {
  padding: '10px 40px 10px 14px',
  background: `#fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2394A3B8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E") no-repeat calc(100% - 12px) center`,
  backgroundSize: '12px 12px',
  border: `1px solid ${BRAND.gray200}`,
  borderRadius: '8px',
  fontSize: 13,
  fontFamily: FONT,
  color: BRAND.gray900,
  outline: 'none',
  cursor: 'pointer',
  minWidth: 180,
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
},

  // dastOverlay: {
  //   position: 'fixed',
  //   inset: 0,
  //   background: 'rgba(0,0,0,0.65)',
  //   backdropFilter: 'blur(6px)',
  //   display: 'flex',
  //   justifyContent: 'center',
  //   alignItems: 'center',
  //   zIndex: 1000,
  // },


};

// ─── Helpers ──────────────────────────────────────────────────────
const StatusDot = ({ status }) => (
  <span style={{ width: 8, height: 8, borderRadius: '50%', background: brand.colors[status?.toLowerCase()] || brand.colors.queued, display: 'inline-block', animation: status === 'RUNNING' || status === 'QUEUED' ? 'pulse 1.5s ease infinite' : 'none' }} />
);

const SeverityBadge = ({ severity }) => {
  const cm = { CRITICAL: brand.colors.critical, Critical: brand.colors.critical, HIGH: brand.colors.high, High: brand.colors.high, MEDIUM: brand.colors.medium, Medium: brand.colors.medium, LOW: brand.colors.low, Low: brand.colors.low, INFORMATIONAL: brand.colors.info, Informational: brand.colors.info };
  return <span style={S.tag(cm[severity] || brand.colors.info)}>{severity}</span>;
};

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const DastScanPage = ({ userData }) => {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [targetUrl, setTargetUrl] = useState("");
  const [scanType, setScanType] = useState("");
  // const [dastStep, setDastStep] = useState(1); // 1=AddDomain, 2=VerifyDNS, 3=Scan, 4=Results
const [dastStep, setDastStep] = useState(1); 



  const [toast, setToast] = useState(null);
  const [analysisModal, setAnalysisModal] = useState(null);
  const [analyzing, setAnalyzing] = useState({});
  const [polling, setPolling] = useState({});
  const [downloading, setDownloading] = useState({});
  const { profile } = useAuth();
  const organizationId = profile.orgId;
  const userId = userData?.["cognito:username"];
  const [zapAlerts, setZapAlerts] = useState(null);
  const [zapSummary, setZapSummary] = useState(null);
  const [selectedScanForFindings, setSelectedScanForFindings] = useState(null);
  const [loadingFindings, setLoadingFindings] = useState(false);
  const [severityFilter, setSeverityFilter] = useState('All');
  const [expandedVuln, setExpandedVuln] = useState(null);

  // const [verificationModal, setVerificationModal] = useState(null);
  // const [verifying, setVerifying] = useState(false);
  const [verifiedDomains, setVerifiedDomains] = useState([]);
  // const [verificationLoading, setVerificationLoading] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
const [checkingConsent, setCheckingConsent] = useState(true);
const [dastEnabled, setDastEnabled] = useState(false);
const [accessInfoOpen, setAccessInfoOpen] = useState(false);
const [requestingAccess, setRequestingAccess] = useState(false);
const [accessRequested, setAccessRequested] = useState(false);
const firstAccessCheckDone = useRef(false);
const [bootstrapped, setBootstrapped] = useState(false);
const [accessRequestLoaded, setAccessRequestLoaded] = useState(false);
const DAST_TEST_MODE = true; // 🔧 Set to false before production /****new for access */

console.log("[DAST] render", {
  organizationId,
  userId,
  bootstrapped,
  checkingConsent,
  dastEnabled,
  consentAccepted,
  accessRequested,
  accessRequestLoaded,
  accessInfoOpen,
});

const getAccessRequestKey = (orgId, userId) =>
  `dast-access-request-${orgId}-${userId}`;
const getAccessStatusKey = (organizationId, userId) => {
  return `dast-access-status-${organizationId}-${userId}`;
};

const checkDastAccess = async (fromRefresh = false) => {
  if (!organizationId || !userId) return;

  // ✅ USE CACHE FIRST
  try {
    const cached = localStorage.getItem(
      getAccessStatusKey(organizationId, userId)
    );

    if (cached === "true" || cached === "false") {
      const enabled = cached === "true";
      setDastEnabled(enabled);
      firstAccessCheckDone.current = true;

      // If not manual refresh, skip API call
      if (!fromRefresh) {
        return;
      }
    }
  } catch {}

  try {
    const data = await scanApi.getDastAccess(organizationId, userId);
    const enabled = data?.dastEnabled === true;
    setDastEnabled(enabled);

    try {
      localStorage.setItem(
        getAccessStatusKey(organizationId, userId),
        enabled ? "true" : "false"
      );
    } catch {}

    if (!firstAccessCheckDone.current) {
      firstAccessCheckDone.current = true;
    }

    if (fromRefresh) {
      if (enabled) {
        setAccessRequested(false);
        try {
          localStorage.removeItem(
            getAccessRequestKey(organizationId, userId)
          );
        } catch {}

        showToast(
          "DAST access granted. You can now use the scanner.",
          "success"
        );
      } else {
        showToast("Access not granted yet.", "error");
      }
    }
  } catch (error) {
    console.error("DAST getDastAccess error", error);
    setDastEnabled(false);

    if (!firstAccessCheckDone.current) {
      firstAccessCheckDone.current = true;
    }

    if (fromRefresh) {
      showToast(
        error?.message || "Failed to check access. Please try again.",
        "error"
      );
    }
  }
};




 const checkConsent = async () => {
  console.log("[DAST] checkConsent start", { organizationId, userId });
  try {
    const data = await scanApi.getConsent(organizationId, userId);
    console.log("[DAST] checkConsent success", data);
    setConsentAccepted(data.hasConsent === true);
  } catch (error) {
    console.log("[DAST] checkConsent error", error);
    setConsentAccepted(false);
  }
};

useEffect(() => {
  if (!organizationId || !userId) {
    console.log("[DAST] bootstrap: no org/user, skipping");
    setCheckingConsent(false);
    setBootstrapped(true);
    return;
  }

  console.log("[DAST] bootstrap init start", { organizationId, userId });
  // const init = async () => {
  //   try {
  //     await checkConsent();

  //     let cachedEnabled = null;
  //     try {
  //       const raw = localStorage.getItem(
  //         getAccessStatusKey(organizationId, userId)
  //       );
  //       console.log("[DAST] bootstrap cached access raw", raw);
  //       if (raw === "true" || raw === "false") {
  //         cachedEnabled = raw === "true";
  //         setDastEnabled(cachedEnabled);
  //         firstAccessCheckDone.current = true;
  //       }
  //     } catch (e) {
  //       console.log("[DAST] bootstrap cached access error", e);
  //     }

  //     if (!firstAccessCheckDone.current) {
  //       console.log("[DAST] bootstrap calling checkDastAccess()");
  //       await checkDastAccess();
  //     } else {
  //       console.log("[DAST] bootstrap skip checkDastAccess, cache used");
  //     }
  //   } finally {
  //     console.log("[DAST] bootstrap finished");
  //     setCheckingConsent(false);
  //     setBootstrapped(true);
  //   }
  // };

/****new for access */
  const init = async () => {
  if (DAST_TEST_MODE) {
    setDastEnabled(true);
    setConsentAccepted(true);
    firstAccessCheckDone.current = true;
    setCheckingConsent(false);
    setBootstrapped(true);
    return;
  }
  // ... rest unchanged

}
  init();
}, [organizationId, userId]);



useEffect(() => {
  if (typeof window === "undefined") return;
  if (!organizationId || !userId) return;
  if (accessRequestLoaded) {
    console.log("[DAST] accessRequested effect: already loaded, skipping");
    return;
  }

  console.log("[DAST] accessRequested effect: loading from localStorage");
  try {
    const key = getAccessRequestKey(organizationId, userId);
    const raw = localStorage.getItem(key);
    console.log("[DAST] accessRequested raw", { key, raw });
    setAccessRequested(raw === "true");
  } catch (e) {
    console.log("[DAST] accessRequested load error", e);
  }

  setAccessRequestLoaded(true);
}, [organizationId, userId, accessRequestLoaded]);


useEffect(() => {
  // We want to auto-open when bootstrapped, no access, regardless of accessRequested.
  if (DAST_TEST_MODE) return;/****new for access */
  const shouldOpenInitially =
    bootstrapped &&
    accessRequestLoaded &&
    !checkingConsent &&
    !dastEnabled;

  console.log("[DAST] accessInfoOpen effect", {
    bootstrapped,
    accessRequestLoaded,
    checkingConsent,
    dastEnabled,
    accessRequested,
    accessInfoOpen,
    shouldOpenInitially,
  });

  // If DAST becomes enabled, always close the modal.
  if (dastEnabled) {
    if (accessInfoOpen) {
      console.log("[DAST] closing accessInfoOpen because dastEnabled = true");
      setAccessInfoOpen(false);
    }
    return;
  }

  // If not enabled yet, keep modal open once it has been opened.
  if (shouldOpenInitially && !accessInfoOpen) {
    console.log("[DAST] opening accessInfoOpen (initial)");
    setAccessInfoOpen(true);
  }
  // Do NOT auto-close when accessRequested flips to true.
}, [
  bootstrapped,
  accessRequestLoaded,
  checkingConsent,
  dastEnabled,
  accessRequested,   // we still log it, but we don't close based on it
  accessInfoOpen,
]);




const AccessInfoModal = ({
  isOpen,
  onClose,
  onRequestAccess,
  submitting,
  onRefreshAccess,
  accessRequested,
}) => {
  if (!isOpen) return null;

  const disableRequest = submitting || accessRequested;
 console.log("[DAST] AccessInfoModal render", {
    isOpen,
    submitting,
    accessRequested,
    disableRequest,
  });
  return (
    <div className="dast-overlay" style={{
  ...S.dastOverlay,
  pointerEvents: 'none'
}}>
      <div
        className="dast-modal-enter"
        style={{ ...S.verifyModal, pointerEvents: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={S.verifyTitle}>DAST Module Access Required</h2>
        <p style={S.verifySubtitle}>
          This module performs Application Security Testing on your web application/website.
          It actively scans to identify security vulnerabilities such as flaws, authentication weaknesses,
          misconfigurations, and other exploitable risks. Once the scan is completed, you receive a detailed
          security report outlining identified vulnerabilities and their severity levels.
        </p>

        <div style={{ ...S.dnsBox, marginTop: 8 }}>
          <p style={{ fontSize: 13, color: brand.colors.textSecondary, marginBottom: 10 }}>
            This module is available as a separate add-on and isn’t included in the free trial or
            standard subscription plans.
          </p>
          <p style={{ fontSize: 13, color: brand.colors.textSecondary, marginBottom: 10 }}>
            Because this involves active security testing, legal domain authorization and consent approval
            are required before activation. The module cannot be enabled under a trial mode.
          </p>
          <p style={{ fontSize: 13, color: brand.colors.textSecondary }}>
            If you would like to access this module, please contact our Support Team for activation details
            and onboarding — we’ll guide you through the setup process.
          </p>
        </div>

        <div style={{ ...S.verifyActions, marginTop: 20 }}>
          <button
            className="dast-btn"
            style={S.btnOutline}
            onClick={onRefreshAccess}
          >
            Refresh Access
          </button>

          <button
            className="dast-btn dast-btn-glow"
            style={{ ...S.btnPrimary, ...(disableRequest ? S.btnDisabled : {}) }}
            disabled={disableRequest}
            onClick={onRequestAccess}
          >
            {submitting ? (
              <>
                <span style={S.spinner} /> Sending Request...
              </>
            ) : accessRequested ? (
              'Request Sent'
            ) : (
              'Request Access'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const handleConsentAccepted = () => {
  // Cache consent in localStorage (keyed by org + user)
  if (userId) {
    try {
      localStorage.setItem(
        `dast-consent-${organizationId}-${userId}`,
        JSON.stringify({
          data: { hasConsent: true, userId },
          cachedAt: Date.now(),
        })
      );
    } catch { /* ignore */ }
  }
  // Clean up old cache key format
  localStorage.removeItem(`dast-consent-${organizationId}`);
  setConsentAccepted(true);
  showToast("Consent recorded! You can now use the DAST Scanner.");
};



  const showToast = (message, type = "success") => { setToast({ message, type }); setTimeout(() => setToast(null), 4000); };

  const loadScans = useCallback(async () => {
    if (!organizationId) return;
    try { const data = await scanApi.listScans(organizationId); setScans(Array.isArray(data) ? data : []); }
    catch (err) { console.error("Failed to load scans:", err); }
    finally { setLoading(false); }
  }, [organizationId]);

  useEffect(() => { loadScans(); }, [loadScans]);

  const ensureParsedInsights = (result) => {
    if (!result) return result;
    let insights = result.aiInsights;
    if (insights && typeof insights === 'object' && insights.executiveSummary) return result;
    if (insights && typeof insights === 'object' && insights.rawAnalysis) {
      let j = insights.rawAnalysis.trim().replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/\s*```$/i, '').trim();
      try { return { ...result, aiInsights: JSON.parse(j) }; } catch (e) { const m = j.match(/\{[\s\S]*\}/); if (m) { try { return { ...result, aiInsights: JSON.parse(m[0]) }; } catch {} } }
      return result;
    }
    if (typeof insights === 'string') {
      let j = insights.trim().replace(/^```json\s*/i, '').replace(/^```\s*/i, '').replace(/\s*```$/i, '').trim();
      try { return { ...result, aiInsights: JSON.parse(j) }; } catch { return { ...result, aiInsights: { rawAnalysis: insights } }; }
    }
    return result;
  };

  const CACHE_TTL = 86400000;
  const getCacheKey = (t, id) => `dast_${t}_${organizationId}_${id}`;
  const getCachedData = (t, id) => { try { const r = localStorage.getItem(getCacheKey(t, id)); if (!r) return null; const c = JSON.parse(r); if (Date.now() - c.timestamp > CACHE_TTL) { localStorage.removeItem(getCacheKey(t, id)); return null; } return c.data; } catch { return null; } };
  const setCachedData = (t, id, d) => { try { localStorage.setItem(getCacheKey(t, id), JSON.stringify({ data: d, timestamp: Date.now() })); } catch {} };

const loadVerifiedDomains = useCallback(async () => {
    if (!organizationId) return;
    try { setVerifiedDomains(await scanApi.listVerifiedDomains(organizationId)); } catch {}
  }, [organizationId]);
 
 
 //DEMOOOOOOOOOOOOOO DEMOOOOOO
//   const loadVerifiedDomains = useCallback(async () => {
//   if (!organizationId) return;
//   try {
//     const data = await scanApi.listVerifiedDomains(organizationId);
//     if (Array.isArray(data) && data.length > 0) {
//       setVerifiedDomains(data);
//     } else {
//       // DEMO: inject a fake verified domain so dropdown works
//       setVerifiedDomains([
//         { domain: 'demo-testsite.com', verifiedAt: Date.now(), method: 'DNS_TXT' },
//         { domain: 'myapp-staging.io',  verifiedAt: Date.now(), method: 'DNS_TXT' },
//       ]);
//     }
//   } catch {
//     // DEMO fallback
//     setVerifiedDomains([
//       { domain: 'demo-testsite.com', verifiedAt: Date.now(), method: 'DNS_TXT' },
//     ]);
//   }
// }, [organizationId]);
  
  
  
  useEffect(() => { loadVerifiedDomains(); }, [loadVerifiedDomains]);

  const loadFindings = async (scanId) => {
    setSelectedScanForFindings(scanId); setSeverityFilter('All'); setExpandedVuln(null); setLoadingFindings(true);
    try {
      const [a, s] = await Promise.all([scanApi.getAlerts(organizationId, scanId), scanApi.getAlertsSummary(organizationId, scanId)]);
      setZapAlerts(a.alerts || []); setZapSummary(s);
      a.totalAlerts === 0 ? showToast('No alerts found yet.', 'error') : showToast(`Loaded ${a.totalAlerts} alerts!`);
    } catch (err) { showToast(err.message, 'error'); setZapAlerts([]); setZapSummary(null); }
    finally { setLoadingFindings(false); }
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault(); if (!targetUrl.trim() || !organizationId) return;
  //   const domain = new URL(targetUrl.trim()).hostname.replace('www.', '');
  //   if (!verifiedDomains.some(d => d.domain === domain)) {
  //     setVerificationLoading(true);
  //     try {
  //       const r = await scanApi.initiateVerification(organizationId, targetUrl.trim());
  //       if (r.status === 'VERIFIED') { showToast('Domain verified! Starting scan...'); await loadVerifiedDomains(); await startScan(); return; }
  //       setVerificationModal({ domain: r.domain, dns: r.dns, verificationId: r.verificationId });
  //     } catch (err) { showToast(err.message, 'error'); } finally { setVerificationLoading(false); }
  //     return;
  //   }
  //   await startScan();
  // };

//new handle submit as Domain is now pre-verified before user reaches the scan form. No need to check again here.
  const handleSubmit = async (e) => {
  e.preventDefault();
  if (!targetUrl.trim() || !organizationId) return;
  await startScan();
};



const startScan = async () => {
    setSubmitting(true);
    try { const r = await scanApi.initiateScan(organizationId, targetUrl.trim(), scanType,userData); showToast(`Scan queued ${r.scanId.slice(0, 8)}...`); setTargetUrl(''); await loadScans(); pollScanStatus(r.scanId); }
    catch (err) { showToast(err.message, 'error'); } finally { setSubmitting(false); }
  };

  // const handleVerifyDomain = async () => {
  //   if (!verificationModal) return; setVerifying(true);
  //   try {
  //     const r = await scanApi.verifyDomain(organizationId, verificationModal.domain);
  //     if (r.status === 'VERIFIED') { showToast(`✓ ${verificationModal.domain} verified!`); setVerificationModal(null); await loadVerifiedDomains(); await startScan(); }
  //     else showToast('DNS record not found. Wait a few minutes.', 'error');
  //   } catch (err) { showToast(err.message, 'error'); } finally { setVerifying(false); }
  // };

  const pollScanStatus = (scanId) => {
    setPolling(p => ({ ...p, [scanId]: true }));
    const iv = setInterval(async () => {
      try {
        const st = await scanApi.getScanStatus(organizationId, scanId);
        if (st.status === 'COMPLETED' || st.status === 'FAILED') {
          clearInterval(iv); setPolling(p => ({ ...p, [scanId]: false }));
          showToast(st.status === 'COMPLETED' ? `Scan completed — ${scanId.slice(0, 8)}` : `Scan failed`, st.status === 'COMPLETED' ? 'success' : 'error');
          await loadScans();
        }
      } catch { clearInterval(iv); setPolling(p => ({ ...p, [scanId]: false })); }
    }, 10000);
  };

  const handleAnalyze = async (scanId, force = false) => {
    if (!force) { const c = getCachedData('analysis', scanId); if (c) { setAnalysisModal(ensureParsedInsights(c)); showToast('Analysis loaded!'); return; } }
    setAnalyzing(p => ({ ...p, [scanId]: true }));
    try {
      const { jobId } = await scanApi.startAnalysis(organizationId, scanId,userData);
      showToast(force ? 'Re-analyzing...' : 'Analysis started...');
      const iv = setInterval(async () => {
        try {
          const r = await scanApi.getAnalysisStatus(organizationId, jobId);
          if (r.status === 'COMPLETED') { clearInterval(iv); setAnalyzing(p => ({ ...p, [scanId]: false })); const pr = ensureParsedInsights(r); setAnalysisModal(pr); setCachedData('analysis', scanId, pr); showToast('Analysis complete!'); }
          else if (r.status === 'FAILED') { clearInterval(iv); setAnalyzing(p => ({ ...p, [scanId]: false })); showToast(`Failed: ${r.error || 'Unknown'}`, 'error'); }
        } catch (err) { clearInterval(iv); setAnalyzing(p => ({ ...p, [scanId]: false })); showToast(err.message, 'error'); }
      }, 5000);
      setTimeout(() => { clearInterval(iv); setAnalyzing(p => { if (p[scanId]) { showToast('Timed out.', 'error'); return { ...p, [scanId]: false }; } return p; }); }, 300000);
    } catch (err) { setAnalyzing(p => ({ ...p, [scanId]: false })); showToast(err.message, 'error'); }
  };
// ── Stop Scan ─────────────────────────────────────────────────────
const handleStopScan = async (scanId) => {
  try {
    await scanApi.stopScan(organizationId, scanId);
    showToast(`Scan ${scanId.slice(0, 8)}... stopped`, 'success');
    await loadScans();
  } catch (err) {
    showToast(err?.message || 'Failed to stop scan', 'error');
  }
};

// ── Delete Scan ───────────────────────────────────────────────────
const handleDeleteScan = async (scanId) => {
  if (!window.confirm('Are you sure you want to delete this scan record?')) return;
  try {
    await scanApi.deleteScan(organizationId, scanId);
    showToast(`Scan deleted`, 'success');
    await loadScans();
  } catch (err) {
    showToast(err?.message || 'Failed to delete scan', 'error');
  }
};
  const handleDownloadPdf = async (scanId) => {
    setDownloading(p => ({ ...p, [scanId]: 'Generating...' }));
    try {
      const g = await scanApi.generateReport(organizationId, scanId,userData);
      if (g.status === 'COMPLETED') { setDownloading(p => ({ ...p, [scanId]: 'Downloading...' })); await downloadPdf(scanId); return; }
      showToast('Report generation started...'); setDownloading(p => ({ ...p, [scanId]: 'Analyzing...' }));
      const iv = setInterval(async () => {
        try {
          const st = await scanApi.getReportStatus(organizationId, scanId);
          if (st.status === 'ANALYZING') setDownloading(p => ({ ...p, [scanId]: 'AI Analyzing...' }));
          else if (st.status === 'GENERATING_PDF') setDownloading(p => ({ ...p, [scanId]: 'Building PDF...' }));
          if (st.status === 'COMPLETED') { clearInterval(iv); setDownloading(p => ({ ...p, [scanId]: 'Downloading...' })); await downloadPdf(scanId); }
          else if (st.status === 'FAILED') { clearInterval(iv); setDownloading(p => ({ ...p, [scanId]: false })); showToast('Report failed.', 'error'); }
        } catch (err) { clearInterval(iv); setDownloading(p => ({ ...p, [scanId]: false })); showToast(err.message, 'error'); }
      }, 5000);
      setTimeout(() => { clearInterval(iv); setDownloading(p => { if (p[scanId]) { showToast('Timed out.', 'error'); return { ...p, [scanId]: false }; } return p; }); }, 300000);
    } catch (err) { setDownloading(p => ({ ...p, [scanId]: false })); showToast(err.message, 'error'); }
  };

  const downloadPdf = async (scanId) => {
    const rpt = await scanApi.getReport(organizationId, scanId);
    if (rpt instanceof Blob) {
      const u = window.URL.createObjectURL(rpt); const a = document.createElement('a'); a.href = u; a.download = `pentest-report-${scanId.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(u); document.body.removeChild(a);
      setDownloading(p => ({ ...p, [scanId]: false })); showToast('PDF downloaded!');
    } else { setDownloading(p => ({ ...p, [scanId]: false })); showToast('Not ready: ' + (rpt.message || 'Retry'), 'error'); }
  };

  const stats = { total: scans.length, queued: scans.filter(s => s.status === 'QUEUED').length, running: scans.filter(s => s.status === 'RUNNING').length, completed: scans.filter(s => s.status === 'COMPLETED').length, failed: scans.filter(s => s.status === 'FAILED').length };

  const sevChartData = useMemo(() => {
    if (!zapAlerts) return [];
    return ['Critical', 'High', 'Medium', 'Low', 'Informational'].map(s => ({
      label: s, value: zapAlerts.filter(a => a.riskLabel?.toLowerCase() === s.toLowerCase()).length,
      color: { Critical: brand.colors.critical, High: brand.colors.high, Medium: brand.colors.medium, Low: brand.colors.low, Informational: brand.colors.info }[s],
    }));
  }, [zapAlerts]);

  const fmt = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return d; } };
  const dur = (a, b) => { if (!a || !b) return '—'; const ms = new Date(b) - new Date(a); const m = Math.floor(ms / 60000); const s = Math.floor((ms % 60000) / 1000); return m > 0 ? `${m}m ${s}s` : `${s}s`; };

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => JSON.parse(localStorage.getItem("isSidebarCollapsed")) || false);



  return (
    
    <Row className="g-0">
      <Col
        xs={12}
        className={`bg-transparent sidebar-container ${
          isSidebarCollapsed ? "collapsed" : "hello"
        }`}
        style={{
          width: isSidebarCollapsed ? "90px" : "317px",
          transition: "width 0.3s ease",
        }}
      >
        <Menubar
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(p => !p)}
        />
      </Col>

      <Col
        className="pt-0 px-0 ps-0 section-container"
        style={{
          width: `calc(100% - ${isSidebarCollapsed ? "90px" : "317px"})`,
          transition: "width 0.3s ease",
          position: "relative",
        }}
      >
        <NavigationBar
          userData={userData}
          isSidebarCollapsed={isSidebarCollapsed}
        />

        {/* while we are checking consent, show only a spinner in content area */}
        {checkingConsent && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "60vh",
            }}
          >
            <span style={S.spinner} />
          </div>
        )}

        {/* DAST access modal (separate overlay, does not hide navbar/menubar) */}
<AccessInfoModal
  isOpen={accessInfoOpen}
  onClose={() => {}}
  submitting={requestingAccess}
  accessRequested={accessRequested}
  onRefreshAccess={() => {
    console.log("[DAST] onRefreshAccess called");
    checkDastAccess(true);
  }}
  onRequestAccess={async () => {
    console.log("[DAST] onRequestAccess handler start", {
      organizationId,
      userId,
      accessRequested,
    });

    if (!organizationId || !userData || accessRequested) {
      console.log("[DAST] onRequestAccess early return", {
        organizationId,
        hasUserData: !!userData,
        accessRequested,
      });
      return;
    }

    const email = profile?.email || userData?.email;
    const name = profile?.name || userData?.name;
    const countryCode = profile?.countryCode || "GB";

    if (!email) {
      console.log("[DAST] onRequestAccess missing email", {
        profileEmail: profile?.email,
        userDataEmail: userData?.email,
      });
      showToast(
        "User email not found. Please update your profile email.",
        "error"
      );
      return;
    }

    try {
      setRequestingAccess(true);
      console.log("[DAST] calling scanApi.requestDastAccess");

      await scanApi.requestDastAccess(organizationId, {
        userId,
        email,
        name,
        regionCode: countryCode,
      });

      console.log("[DAST] requestDastAccess success");

      setAccessRequested(true);
      try {
        const key = getAccessRequestKey(organizationId, userId);
        localStorage.setItem(key, "true");
        console.log("[DAST] accessRequested saved to localStorage", { key });
      } catch (e) {
        console.log("[DAST] accessRequested localStorage save error", e);
      }

      showToast("Your access request has been sent to our team.", "success");
    } catch (err) {
      console.log("[DAST] requestDastAccess error", err);
      showToast(
        err?.message || "Failed to send access request",
        "error"
      );
    } finally {
      console.log("[DAST] onRequestAccess handler end");
      setRequestingAccess(false);
    }
  }}
/>




        {/* ─── DAST MODULE (only when DAST enabled) ─── */}
        {dastEnabled && (
          <>
            {/* consent modal */}
            <ScanConsentModal
              isOpen={dastEnabled && !consentAccepted && !checkingConsent}
              onClose={() => {}}
              onConsentAccepted={handleConsentAccepted}
              targetUrl=""
              organizationId={organizationId}
              profile={profile}
              userData={userData}
              showToast={showToast}
            />

            {/* main module only after consent */}
            {consentAccepted && (
              <div className="overflow-auto dast-v2">
                <div style={S.page}>
                  <div
                    style={S.inner}
                    className="dast-page-pad dast-v2-inner"
                  >
                    {/* ═══ HEADER ═══ */}
                    <div style={S.header} className="dast-header-flex">
                      <div style={S.titleWrap}>
                        <div>
                          <h1 style={S.title}>DAST Scanner</h1>
                          <p style={S.subtitle}>
                            Application Security Testing • Automated
                            Vulnerability Detection
                          </p>
                        </div>
                      </div>
                      <div style={S.headerRight}>
                        {/* <button
                          className="dast-btn"
                          style={S.btnOutline}
                          onClick={loadScans}
                        >
                          ↻ Refresh
                        </button> */}
                      </div>
                    </div>

          {/* ═══ SCAN FORM ═══ */}
          {/* DOMAIN VERIFICATION SECTION */}
<DomainManagement
  organizationId={organizationId}
  showToast={showToast}
  onDomainVerified={loadVerifiedDomains}
  dastStep={dastStep}
  setDastStep={setDastStep}
/>

{/* SCAN FORM + STATS + HISTORY — only visible after domain verified */}
{dastStep >= 3 && (
<>
          <div id="dast-scan-form" style={S.formCard} className="dast-card-static">
            <div style={S.formAccent} />
            <form onSubmit={handleSubmit} style={S.formRow} className="dast-form-flex">
            <div style={{ ...S.inputGroup, marginLeft: "-4px" }}>
                <label style={S.label}>Target URL</label>
                {/* <input
                  style={S.input}
                  type="url"
                  placeholder="https://example.com"
                  value={targetUrl}
                  onChange={e => setTargetUrl(e.target.value)}
                  required
                  onFocus={e => {
                    e.target.style.borderColor = brand.colors.primary;
                    e.target.style.boxShadow = `0 0 0 3px ${brand.colors.primary}15`;
                  }}
                  onBlur={e => {
                    e.target.style.borderColor = brand.colors.surfaceBorder;
                    e.target.style.boxShadow = "none";
                  }}
                />
                {targetUrl && (() => {
                  try {
                    const d = new URL(targetUrl).hostname.replace("www.", "");
                    const v = verifiedDomains.some(x => x.domain === d);
                    return (
                      <div style={S.badge_domain(v)}>
                        {v ? "✓ Domain verified" : "⚠ Verification required"}
                      </div>
                    );
                  } catch {
                    return null;
                  }
                })()} */}

                  {/* Only verified domains appear in the dropdown. User cannot accidentally try to scan an unverified domain. */}
        <select
  style={{ ...S.select, flex: 1,color: targetUrl ? BRAND.gray900 : BRAND.gray400 }}
  value={targetUrl}
  onChange={(e) => setTargetUrl(e.target.value)}
  onMouseEnter={(e) => {
    e.target.style.borderColor = BRAND.secondary;
  }}
  onMouseLeave={(e) => {
    if (document.activeElement !== e.target) {
      e.target.style.borderColor = BRAND.gray200;
      e.target.style.boxShadow = "none";
    }
  }}
  onFocus={(e) => {
    e.target.style.borderColor = BRAND.secondary;
    e.target.style.boxShadow = `0 0 0 3px ${BRAND.secondaryLight}`;
  }}
  onBlur={(e) => {
    e.target.style.borderColor = BRAND.gray200;
    e.target.style.boxShadow = "none";
  }}
  required
>
  <option value="">Select a verified domain</option>
  {verifiedDomains.map((d) => (
    <option key={d.domain} value={`https://${d.domain}`}>
      {d.domain}
    </option>
  ))}
</select>
              </div>
              <div>
                <label style={S.label}>Scan Type</label>
               <select
  style={{
  ...S.select,
  color: scanType ? BRAND.gray900 : BRAND.gray400
}}
  value={scanType}
  onChange={e => setScanType(e.target.value)}
  onMouseEnter={(e) => {
    e.target.style.borderColor = BRAND.secondary;
  }}
  onMouseLeave={(e) => {
    if (document.activeElement !== e.target) {
      e.target.style.borderColor = BRAND.gray200;
      e.target.style.boxShadow = "none";
    }
  }}
  onFocus={(e) => {
    e.target.style.borderColor = BRAND.secondary;
    e.target.style.boxShadow = `0 0 0 3px ${BRAND.secondaryLight}`;
  }}
  onBlur={(e) => {
    e.target.style.borderColor = BRAND.gray200;
    e.target.style.boxShadow = "none";
  }}
>
  <option value="">Select Scan Type</option>
  <option value="FULL">Full Scan</option>
  <option value="QUICK">Quick Scan</option>
</select>
              </div>
              <button
                type="submit"
                className="dast-btn dast-btn-glow"
                style={{ ...S.btnPrimary, ...(submitting ? S.btnDisabled : {}), marginTop: 22}}
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span style={S.spinner} /> Queuing...
                  </>
                ) : (
                  <>Launch Scan</>
                )}
              </button>
            </form>
          </div>

          {/* ═══ STATS ═══ */}
          {/* <div style={S.statsGrid} className="dast-grid-5 dast-stagger">
            {[
              { label: "Total Scans", value: stats.total, color: brand.colors.text, icon:""},
              { label: "Queued", value: stats.queued, color: brand.colors.queued || "#6B7280", icon: "" },
              { label: "Running", value: stats.running, color: "#0A7FDC", icon: "" },
              { label: "Completed", value: stats.completed, color: brand.colors.primary, icon: "" },
              { label: "Failed", value: stats.failed, color: brand.colors.critical, icon: ""},
            ].map(({ label, value, color, icon }) => (
              <div key={label} className="dast-card" style={S.statCard(color, value > 0)}>

                
                <div style={S.statAccent(color)} />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    paddingLeft: 8,
                  }}
                >
                  <div>
                    <p style={{ ...S.statVal, color, animation: "countUp 0.5s ease" }}>{value}</p>
                    <p style={S.statLbl}>{label}</p>
                  </div>
                  <div style={S.statIconBg(color)}>{icon}</div>
                </div>
              </div>
            ))}
          </div> */}

{/* ═══ STATS ═══ */}
<div style={S.statsGrid} className="dast-grid-5 dast-stagger">
  {[
    { label: "Total Scans", value: stats.total, color: brand.colors.text, icon: "" },
    { label: "Queued", value: stats.queued, color: brand.colors.queued || "#6B7280", icon: "" },
    { label: "Running", value: stats.running, color: "#0A7FDC", icon: "" },
    { label: "Completed", value: stats.completed, color: brand.colors.primary, icon: "" },
    { label: "Failed", value: stats.failed, color: brand.colors.critical, icon: "" },
  ].map(({ label, value, color, icon }) => (
    <div key={label} className="dast-card dast-stat-card" style={S.statCard(color, value > 0)}>

      {/* <div className="dast-stat-accent" style={S.statAccent(color)} /> */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          paddingLeft: 8,
        }}
      >
        <div>
          <p className="dast-stat-val" style={{ ...S.statVal,  color: '#000',animation: "countUp 0.5s ease"}}>
            {value}
          </p>
          <p className="dast-stat-lbl" style={S.statLbl}>{label}</p>
        </div>
        <div className="dast-stat-icon" style={S.statIconBg(color)}>{icon}</div>
      </div>

    </div>
  ))}
</div>




          {/* ═══ OVERVIEW CHARTS ═══ */}
          {scans.length > 0 && (
            <div style={S.overviewRow} className="dast-chart-grid">
              <div style={S.overviewCard} className="dast-card-static">
                <div style={S.overviewTitle}>Scan Status Overview</div>
                <ScanOverviewChart stats={stats} />
              </div>
              <div style={S.overviewCard} className="dast-card-static">
                <div style={S.overviewTitle}>Recent Activity</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {scans.slice(0, 4).map(s => (
                    <div
                      key={s.scanId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 14px",
                        background: brand.colors.surface,
                        borderRadius: brand.radius.sm,
                        border: `1px solid ${brand.colors.surfaceBorder}`,
                      }}
                    >
                      <StatusDot status={s.status} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: brand.colors.text,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {s.targetUrl}
                        </div>
                        <div style={{ fontSize: 11, color: brand.colors.textMuted }}>
                          {fmt(s.createdAt)}
                        </div>
                      </div>
                      <span style={S.badge(s.status)}>{s.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ═══ SCAN TABLE ═══ */}
          <div style={S.tableCard} className="dast-card-static">
            <div style={S.tableHead}>
              <span style={S.tableTitleText}>
                 SCAN HISTORY{" "}
                <span
                  style={{
                    fontSize: 13,
                     color: '#64748B', fontFamily: brand.font,
                    fontWeight: 500,
                   
                    marginLeft: 6,
                  }}
                >
                  {scans.length} records
                </span>
              </span>
            </div>
            <div className="dast-table-wrap">
              {loading ? (
                <div style={{ textAlign: "center", padding: 60 }}>
                  <span style={{ ...S.spinner, width: 28, height: 28 }} />
                </div>
              ) : scans.length === 0 ? (
                <div style={S.empty}>
                  <div style={S.emptyIcon}>🔍</div>
                  <p style={S.emptyText}>No scans yet. Launch your first scan above.</p>
                </div>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      {["Scan ID", "Target", "Status", "Started", "Duration", "Actions"].map(h => (
                        <th key={h} style={S.th}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {scans
                      .sort(
                        (a, b) =>
                          new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
                      )
                      .map(scan => (
                        <tr key={scan.scanId} className="dast-tr">
                          <td style={{ ...S.td, fontFamily: "monospace", fontSize: 13 }}>
                            {scan.scanId?.slice(0, 12)}...
                          </td>
                          <td style={S.td}>
                            <a
                              href={scan.targetUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: brand.colors.secondaryLight,
                                textDecoration: "none",
                                fontWeight: 500,
                              }}
                            >
                              {scan.targetUrl}
                            </a>
                          </td>
                          <td style={S.td}>
                            <span style={S.badge(scan.status)}>
                      
                              {scan.status}
                              {polling[scan.scanId] && (
                                <span style={S.spinner} />
                              )}
                            </span>
                          </td>
                          <td
                            style={{
                              ...S.td,
                              color: brand.colors.textSecondary,
                              fontSize: 13,
                            }}
                          >
                            {fmt(scan.createdAt)}
                          </td>
                          <td
                            style={{
                              ...S.td,
                              color: brand.colors.textSecondary,
                              fontSize: 13,
                            }}
                          >
                            {dur(scan.startedAt, scan.completedAt)}
                          </td>
                
                  <td style={S.td}>
  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>

    {/* ── Track — QUEUED or RUNNING, not yet polling ── */}
    {(scan.status === 'QUEUED' || scan.status === 'RUNNING') && !polling[scan.scanId] && (
      <button
        className="dast-btn"
        style={S.btnOutline}
        onClick={() => pollScanStatus(scan.scanId)}
      >
        Track
      </button>
    )}

    {/* ── Stop — QUEUED or RUNNING ── */}
    {(scan.status === 'QUEUED' || scan.status === 'RUNNING') && (
      <button
        className="dast-btn"
        style={{
          ...S.btnOutline,
          fontSize: 12,
          padding: '6px 12px',
          color: '#DC2626',
          borderColor: '#FECACA',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
        onClick={() => handleStopScan(scan.scanId)}
        title="Stop this scan"
      >
        ⏹ Stop
      </button>
    )}

    {/* ── AI Analysis — COMPLETED only ── */}
    {scan.status === 'COMPLETED' && (
      <button
        className="dast-btn dast-btn-glow"
        style={{
          ...S.btnAnalyze,
          ...(analyzing[scan.scanId] ? S.btnDisabled : {}),
        }}
        onClick={() => handleAnalyze(scan.scanId)}
        disabled={!!analyzing[scan.scanId]}
        title="Run AI Analysis"
      >
        {analyzing[scan.scanId]
          ? <><span style={S.spinner} /> Analyzing...</>
          : 'AI Analysis'
        }
      </button>
    )}

    {/* ── PDF — COMPLETED only ── */}
    {scan.status === 'COMPLETED' && (
      <button
        className="dast-btn"
        style={{
          ...S.btnOutline,
          ...(downloading[scan.scanId] ? S.btnDisabled : {}),
        }}
        onClick={() => handleDownloadPdf(scan.scanId)}
        disabled={!!downloading[scan.scanId]}
        title="Download PDF Report"
      >
        {downloading[scan.scanId]
          ? <><span style={S.spinner} /> {downloading[scan.scanId]}...</>
          : 'PDF'
        }
      </button>
    )}

    {/* ── Delete — always visible ── */}
    <button
      className="dast-btn"
      style={{
        padding: '6px 10px',
        background: '#FEF2F2',
        color: '#DC2626',
        border: '1px solid #FECACA',
        borderRadius: '8px',
        fontSize: 12,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: FONT,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}
      onClick={() => handleDeleteScan(scan.scanId)}
      title="Delete this scan record"
    >
      🗑
    </button>

  </div>
</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

              {/* ═══ FINDINGS ═══ */}
              <div style={S.findingsSection}>
                <div style={S.findingsHeader}>
                  <div>
                    <div style={S.findingsTitle}>Vulnerability Findings</div>
                    <div style={S.findingsSubtitle}>
                      {zapAlerts ? `Showing ${zapAlerts.length} alerts for scan ${selectedScanForFindings?.slice(0, 8)}...` : 'Select a completed scan to view findings'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {zapAlerts && selectedScanForFindings && !loadingFindings && (
                      <button className="dast-btn" style={S.btnReanalyze} onClick={() => loadFindings(selectedScanForFindings)}>↻ Refresh</button>
                    )}
                   <select
  style={{ ...S.scanSelector, position: 'relative', zIndex: 2, color: selectedScanForFindings ? BRAND.gray900 : BRAND.gray400 }}
  value={selectedScanForFindings || ''}
  onChange={e => { e.target.value && loadFindings(e.target.value); }}
  onMouseEnter={e => e.target.style.borderColor = BRAND.secondary}
  onMouseLeave={e => { if (document.activeElement !== e.target) e.target.style.borderColor = BRAND.gray200; e.target.style.boxShadow = 'none'; }}
  onFocus={e => { e.target.style.borderColor = BRAND.secondary; e.target.style.boxShadow = `0 0 0 3px ${BRAND.secondaryLight}`; }}
  onBlur={e => { e.target.style.borderColor = BRAND.gray200; e.target.style.boxShadow = 'none'; }}
>
  <option value="">Select a scan...</option>
   {scans.filter(s => s.status === 'COMPLETED').map(s => (
                        <option key={s.scanId} value={s.scanId}>{s.scanId?.slice(0, 8)}... — {s.targetUrl}</option>
                      ))}
</select>
                  </div>
                </div>

                {loadingFindings && <div style={{ textAlign: 'center', padding: 60 }}><span style={{ ...S.spinner, width: 28, height: 28 }} /><p style={{ color: brand.colors.textSecondary, marginTop: 12 }}>Loading alerts...</p></div>}

                {zapAlerts && !loadingFindings && (() => {
                  const sevs = ['Critical', 'High', 'Medium', 'Low', 'Informational'];
                  const sevC = { Critical: brand.colors.critical, High: brand.colors.high, Medium: brand.colors.medium, Low: brand.colors.low, Informational: brand.colors.info };
                  const filtered = severityFilter === 'All' ? zapAlerts : zapAlerts.filter(a => a.riskLabel?.toLowerCase() === severityFilter.toLowerCase());
                  return (<>
                    {/* Charts */}
                    <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 24, marginBottom: 24 }} className="dast-chart-grid">
                      <div style={{ ...S.chartCard, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <DonutChart data={sevChartData.filter(d => d.value > 0)} />
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 16, justifyContent: 'center' }}>
                          {sevChartData.filter(d => d.value > 0).map(d => (
                            <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: brand.colors.textSecondary }}>
                              <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.color }} />{d.label}
                            </div>
                          ))}
                        </div>
                      </div>
                      <div style={S.chartCard}>
                        <div style={S.chartTitle}>Severity Distribution</div>
                        <HBarChart data={sevChartData} />
                      </div>
                    </div>

                    {/* Summary cards */}
                    <div style={S.findingsSummaryBar} className="dast-grid-5 dast-stagger">
                      {sevs.map(sev => {
                        const cnt = zapAlerts.filter(a => a.riskLabel?.toLowerCase() === sev.toLowerCase()).length;
                        return (
                          <div key={sev} className="dast-card" style={S.findingsSummaryItem(sevC[sev], severityFilter === sev)}
                            onClick={() => setSeverityFilter(severityFilter === sev ? 'All' : sev)}>
                            <div style={{ fontSize: 30, fontWeight: 800, color: sevC[sev], lineHeight: 1 }}>{cnt}</div>
                            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: brand.colors.textSecondary, marginTop: 8 }}>{sev}</div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Filters */}
                    <div style={S.filterBar}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: brand.colors.textMuted, marginRight: 4 }}>Filter:</span>
                      {['All', ...sevs].map(s => (
                        <span key={s} className="dast-pill" style={S.filterPill(severityFilter === s, sevC[s] || brand.colors.primary)} onClick={() => setSeverityFilter(s)}>
                          {s} ({s === 'All' ? zapAlerts.length : zapAlerts.filter(a => a.riskLabel?.toLowerCase() === s.toLowerCase()).length})
                        </span>
                      ))}
                    </div>

                    {/* Vuln rows */}
                    {filtered.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: 40, color: brand.colors.textSecondary }}>No {severityFilter !== 'All' ? severityFilter : ''} vulnerabilities found.</div>
                    ) : filtered.map((alert, i) => {
                      const exp = expandedVuln === i;
                      const sev = alert.riskLabel || 'Informational';
                      return (
                        <div key={alert.alertId || i} className="dast-vuln" style={S.vulnRow(sev)}>
                          <div style={S.vulnHead} onClick={() => setExpandedVuln(exp ? null : i)}>
                            <div style={S.vulnLeft}>
                              <SeverityBadge severity={sev} />
                              <span style={S.vulnName}>{alert.name}</span>
                            </div>
                            <div style={S.vulnMeta}>
                              {alert.cweid && <span style={S.tag(brand.colors.secondary)}>CWE-{alert.cweid}</span>}
                              {alert.confidence && <span style={S.tag(brand.colors.info)}>{alert.confidence}</span>}
                              <span style={{ fontSize: 12, color: brand.colors.textMuted, marginLeft: 8 }}>{alert.instanceCount || 0} instance{alert.instanceCount !== 1 ? 's' : ''}</span>
                              <span style={{ transform: exp ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.25s', fontSize: 14, color: brand.colors.textMuted }}>▼</span>
                            </div>
                          </div>
                          {exp && (
                            <div style={S.vulnBody}>
                              <div style={S.vulnGrid} className="dast-detail-grid">
                                <div style={S.vulnBlock}><div style={S.vulnBlockLabel}>Description</div><div style={S.vulnBlockText}>{alert.description}</div></div>
                                <div style={S.vulnBlock}><div style={S.vulnBlockLabel}>Solution</div><div style={{ ...S.vulnBlockText, color: brand.colors.primary }}>{alert.solution}</div></div>
                                {alert.otherInfo && <div style={{ ...S.vulnBlock, gridColumn: '1 / -1' }}><div style={S.vulnBlockLabel}>Additional Info</div><div style={S.vulnBlockText}>{alert.otherInfo}</div></div>}
                              </div>
                              {alert.instances?.length > 0 && (
                                <div style={{ marginTop: 16 }}>
                                  <div style={S.vulnBlockLabel}>Affected URLs ({alert.instances.length})</div>
                                  <div style={{ marginTop: 6 }}>{alert.instances.map((inst, j) => <span key={j} style={S.urlChip}>{inst.method} {inst.uri}</span>)}</div>
                                </div>
                              )}
                              <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                                {alert.pluginId && <span style={S.tag(brand.colors.textMuted)}>Plugin: {alert.pluginId}</span>}
                                {alert.wascId && <span style={S.tag(brand.colors.medium)}>WASC-{alert.wascId}</span>}
                                {alert.reference && <span style={S.tag(brand.colors.secondary)}>Has References</span>}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </>);
                })()}
              </div>
 </>
)}

              {/* ═══ VERIFY MODAL ═══ */}
              {/* {verificationModal && (
                <div className="dast-overlay" style={S.verifyOverlay} onClick={() => setVerificationModal(null)}>
                  <div className="dast-modal-enter" style={S.verifyModal} onClick={e => e.stopPropagation()}>
                    <div style={S.verifyTitle}>🔐 Domain Verification Required</div>
                    <div style={S.verifySubtitle}>Verify ownership of <strong>{verificationModal.domain}</strong> before scanning.</div>
                    <div style={S.dnsBox}>
                      <div style={S.dnsLabel}>Add this DNS TXT Record</div>
                      <div style={{ marginBottom: 12 }}><div style={{ fontSize: 12, color: brand.colors.textMuted, marginBottom: 4 }}>Record Type</div><div style={{ fontSize: 14, fontWeight: 700 }}>TXT</div></div>
                      <div style={{ marginBottom: 12 }}><div style={{ fontSize: 12, color: brand.colors.textMuted, marginBottom: 4 }}>Host</div><div style={{ fontSize: 14, fontWeight: 700 }}>@ </div></div>
                      <div><div style={{ fontSize: 12, color: brand.colors.textMuted, marginBottom: 4 }}>Value (click to copy)</div>
                        <div style={S.dnsValue} onClick={() => { navigator.clipboard.writeText(verificationModal.dns.value); showToast('Copied!'); }}>{verificationModal.dns.value}</div>
                      </div>
                    </div>
                    <div style={S.verifySteps}><strong style={{ color: brand.colors.text }}>Step 2</strong> — Add the record at your DNS provider.<br /><strong style={{ color: brand.colors.text }}>Step 3</strong> — Wait 2–5 min, then click <strong>Verify Now</strong>.</div>
                    <div style={S.verifyActions}>
                      <button className="dast-btn" style={S.btnOutline} onClick={() => setVerificationModal(null)}>Cancel</button>
                      <button className="dast-btn dast-btn-glow" style={{ ...S.btnPrimary, ...(verifying ? S.btnDisabled : {}) }} disabled={verifying} onClick={handleVerifyDomain}>
                        {verifying ? <><span style={S.spinner} /> Checking DNS...</> : '✓ Verify Now'}
                      </button>
                    </div>
                  </div>
                </div>
              )} */}

              {/* ═══ ANALYSIS MODAL ═══ */}
              {analysisModal && (
                <div className="dast-overlay" style={S.overlay} onClick={() => setAnalysisModal(null)}>
                  <div className="dast-modal-enter dast-modal-box" style={S.modal} onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                      <h2 style={S.modalTitle}>AI Security Analysis</h2>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button className="dast-btn" style={{ ...S.btnReanalyze, ...(analyzing[analysisModal.scanId] ? S.btnDisabled : {}) }}
                          disabled={analyzing[analysisModal.scanId]} onClick={() => { const id = analysisModal.scanId; setAnalysisModal(null); handleAnalyze(id, true); }}>
                          {analyzing[analysisModal.scanId] ? <><span style={S.spinner} /> Re-analyzing...</> : '↻ Re-analyze'}
                        </button>
                        <button onClick={() => setAnalysisModal(null)} style={{ background: 'none', border: 'none', color: brand.colors.textMuted, fontSize: 28, cursor: 'pointer' }}>×</button>
                      </div>
                    </div>

                    {analysisModal.aiInsights?.executiveSummary && (<>
                      <div style={{ ...S.overviewCard, marginBottom: 20, borderLeft: `4px solid ${brand.colors.primary}` }}>
                        <h3 style={{ fontSize: 13, fontWeight: 700, color: brand.colors.primary, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Executive Summary</h3>
                        <p style={{ fontSize: 14, lineHeight: 1.8, color: brand.colors.textSecondary }}>{analysisModal.aiInsights.executiveSummary.overview}</p>
                        <div style={{ display: 'flex', gap: 24, marginTop: 12 }}>
                          <span style={{ fontSize: 13, color: brand.colors.textMuted }}>Risk: <strong style={{ color: brand.colors[analysisModal.aiInsights.executiveSummary.riskRating?.toLowerCase()] || brand.colors.text }}>{analysisModal.aiInsights.executiveSummary.riskRating}</strong></span>
                          <span style={{ fontSize: 13, color: brand.colors.textMuted }}>Total: <strong style={{ color: brand.colors.text }}>{analysisModal.aiInsights.executiveSummary.totalVulnerabilities}</strong></span>
                        </div>
                      </div>

                      {analysisModal.aiInsights.riskMatrix && (
                        <div style={S.riskGrid} className="dast-risk-grid dast-stagger">
                          {Object.entries(analysisModal.aiInsights.riskMatrix).map(([sev, cnt]) => (
                            <div key={sev} style={S.riskItem(sev)}>
                              <p style={{ fontSize: 28, fontWeight: 800, margin: 0, color: brand.colors[sev] || brand.colors.text }}>{cnt}</p>
                              <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: brand.colors.textSecondary, marginTop: 4 }}>{sev}</p>
                            </div>
                          ))}
                        </div>
                      )}

                      {analysisModal.aiInsights.vulnerabilities?.length > 0 && (
                        <div style={{ marginBottom: 24 }}>
                          <h3 style={{ fontSize: 13, fontWeight: 700, color: brand.colors.text, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Findings ({analysisModal.aiInsights.vulnerabilities.length})
                          </h3>
                          {analysisModal.aiInsights.vulnerabilities.map((v, i) => (
                            <div key={i} style={S.vulnCard(v.severity)}>
                              <div style={S.vulnTitle2}>{v.name}</div>
                              <div style={S.vulnMeta2}><SeverityBadge severity={v.severity} />{v.cweId && <span style={S.tag(brand.colors.secondary)}>{v.cweId}</span>}{v.priority && <span style={S.tag(brand.colors.textMuted)}>{v.priority}</span>}</div>
                              <div style={S.vulnSec}><strong style={{ color: brand.colors.text }}>Description:</strong> {v.description}</div>
                              {v.affectedUrls?.length > 0 && <div style={{ ...S.vulnSec, fontFamily: 'monospace', fontSize: 12 }}><strong style={{ color: brand.colors.text }}>Affected:</strong> {v.affectedUrls.join(', ')}</div>}
                              <div style={S.vulnSec}><strong style={{ color: brand.colors.text }}>Impact:</strong> {v.impact}</div>
                              <div style={{ ...S.vulnSec, color: brand.colors.primary }}><strong>Remediation:</strong> {v.remediation}</div>
                            </div>
                          ))}
                        </div>
                      )}

                      {analysisModal.aiInsights.remediationPlan?.length > 0 && (
                        <div style={{ marginBottom: 24 }}>
                          <h3 style={{ fontSize: 13, fontWeight: 700, color: brand.colors.text, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 0.5 }}>Remediation Plan</h3>
                          {analysisModal.aiInsights.remediationPlan.map((item, i) => (
                            <div key={i} style={{ display: 'flex', gap: 16, padding: '12px 0', borderBottom: `1px solid ${brand.colors.divider}` }}>
                              <span style={{ width: 28, height: 28, borderRadius: '50%', background: brand.colors.primaryBg, color: brand.colors.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{item.priority}</span>
                              <div>
                                <div style={{ fontSize: 14, fontWeight: 500, color: brand.colors.text }}>{item.vulnerability}</div>
                                <div style={{ fontSize: 13, color: brand.colors.textSecondary, marginTop: 4 }}>{item.action}</div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                                  <span style={S.tag(brand.colors.secondary)}>{item.effort}</span>
                                  <span style={S.tag(brand.colors.high)}>{item.deadline}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>)}

                    {analysisModal.aiInsights?.rawAnalysis && (
                      <pre style={{ background: brand.colors.cardBg, padding: 20, borderRadius: brand.radius.md, fontSize: 13, lineHeight: 1.7, color: brand.colors.textSecondary, whiteSpace: 'pre-wrap', overflowX: 'auto' }}>
                        {analysisModal.aiInsights.rawAnalysis}
                      </pre>
                    )}
                  </div>
                </div>
              )}

              {/* ═══ TOAST ═══ */}
             {toast && (
                      <div style={S.toast(toast.type)}>
                        {toast.type === "error" ? "⚠️" : "✅"} {toast.message}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <BackgroundFooter isSidebarCollapsed={isSidebarCollapsed} />
      </Col>
    </Row>
  );
};

export default DastScanPage;